/**
 * DeepSeek API facts — single source of truth for everything on this site that
 * is not a price: model names, limits, endpoints, error codes.
 * Prices stay in src/lib/deepseek-pricing.ts.
 *
 * Every value below was checked against the official docs on `lastVerified`.
 * When DeepSeek changes something, update it HERE and in seo/stale-terms.json,
 * then run `node scripts/seo-brief.mjs --stale-scan` to find pages that still
 * say the old thing.
 */

export const DEEPSEEK_API = {
  lastVerified: "2026-11-03",

  baseUrl: "https://api.deepseek.com",
  anthropicBaseUrl: "https://api.deepseek.com/anthropic",
  chatPath: "/chat/completions",
  platformUrl: "https://platform.deepseek.com",
  apiKeysUrl: "https://platform.deepseek.com/api_keys",
  statusUrl: "https://status.deepseek.com/",

  models: {
    flash: {
      apiName: "deepseek-flash",
      version: "DeepSeek-V4.1-Flash-1017",
      label: "DeepSeek-V4.1-Flash",
      contextTokens: "1M",
      maxOutputTokens: "384K",
      concurrency: 2500,
      vision: true,
      bestFor: "Default for chat, agents, tool use, RAG and vision — fast and cheapest",
    },
    pro: {
      apiName: "deepseek-v4-pro",
      version: "DeepSeek-V4-Pro-0813",
      label: "DeepSeek-V4-Pro",
      contextTokens: "1M",
      maxOutputTokens: "384K",
      concurrency: 500,
      vision: false,
      bestFor: "Hardest reasoning, long multi-step coding and math",
    },
  },

  // Thinking mode is ON by default for both models.
  thinking: {
    defaultOn: true,
    effortLevels: ["low", "high", "max"],
    defaultEffort: "high",
    defaultMaxTokens: { nonThinking: "8K", thinking: "64K", thinkingMax: "128K" },
  },

  legacyNames: [
    {
      names: ["deepseek-v4-flash", "deepseek-v4-flash-vision-exp"],
      status: "Still accepted, temporarily routed to DeepSeek-V4.1-Flash and billed at the Flash price (since 10 September 2026).",
      replaceWith: "deepseek-flash",
    },
    {
      names: ["deepseek-chat", "deepseek-reasoner"],
      status: "Scheduled for discontinuation on 24 July 2026 and no longer in the official model list.",
      replaceWith: "deepseek-flash (thinking off for the old chat behaviour, on for reasoner)",
    },
  ],

  searchFetch: {
    pricePer1000: 0.75,
    note: "Search and Fetch are billed per successful request; billing for server-side tool calls from DeepSeek Harness, Claude Code and Codex started on 1 November 2026 (00:00 Beijing time).",
  },

  peakHoursUTC: "01:00–04:00 and 06:00–10:00 UTC, Monday to Friday, excluding Chinese public holidays",

  errors: [
    { code: 400, name: "Invalid Format", cause: "The request body is malformed.", fix: "Fix the body using the hint in the error message." },
    { code: 401, name: "Authentication Fails", cause: "Wrong or revoked API key.", fix: "Check the key, or create a new one on the platform." },
    { code: 402, name: "Insufficient Balance", cause: "The account has no balance left.", fix: "Top up on the platform, then retry." },
    { code: 422, name: "Invalid Parameters", cause: "A parameter value is not allowed.", fix: "Correct the parameter named in the error message." },
    { code: 429, name: "Rate Limit Reached", cause: "Too many requests at once (concurrency limit).", fix: "Back off and retry, queue requests, or ask for a higher limit." },
    { code: 500, name: "Server Error", cause: "A problem on DeepSeek's side.", fix: "Retry after a short wait; contact DeepSeek if it persists." },
    { code: 503, name: "Server Overloaded", cause: "High traffic on DeepSeek's side.", fix: "Retry after a short wait." },
  ],

  sources: [
    { title: "Models & Pricing", url: "https://api-docs.deepseek.com/quick_start/pricing/" },
    { title: "Your First API Call", url: "https://api-docs.deepseek.com/" },
    { title: "Chat Completions API reference", url: "https://api-docs.deepseek.com/api/create-chat-completion/" },
    { title: "Rate Limit & Isolation", url: "https://api-docs.deepseek.com/quick_start/rate_limit/" },
    { title: "Error Codes", url: "https://api-docs.deepseek.com/quick_start/error_codes/" },
    { title: "Using the Anthropic API", url: "https://api-docs.deepseek.com/guides/anthropic_api/" },
    { title: "Thinking Mode", url: "https://api-docs.deepseek.com/guides/thinking_mode/" },
    { title: "Integrate with Claude Code", url: "https://api-docs.deepseek.com/quick_start/agent_integrations/claude_code" },
    { title: "Change Log", url: "https://api-docs.deepseek.com/updates/" },
  ],
} as const;
